import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { IchraRenewalTaskIds, RenewalPlanPeriodResponse, TaskStatus, getRenewalPlanPeriod } from 'core'
import { PlanPeriodDto, getActivePlanPeriod } from 'core/application/adminDashboard/benefitDetails/benefitPlanPeriods'
import { BenefitDesignStatus } from 'core/domain/benefit/benefitDesignStatus'

import { Skeleton } from 'common/components'
import { formatYear, parseZoneFormat } from 'common/dateFormatters'
import { useBreakPoints, useQueryRequestV2, useRenewalTasks, useSharedState } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import Icon from 'assets/images/perks-dashboard-home-setup-icon.svg'

import SetupProgress from '../../common/adminTaskStep/SetupProgress'
import SetupProgressSteps from '../../common/adminTaskStep/SetupProgressSteps'
import InviteIchraRenewalEmployeesModal from './InviteRenewalEmployeesModal'

const IchraRenewalTasks = () => {
  const history = useHistory()
  const [tasks, isLoading, reloadTasks] = useRenewalTasks()
  const [inviteEmployeesModalVisible, setInviteEmployeesModalVisible] = useState<boolean>()
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0)
  const { data: activePlanPeriod } = useQueryRequestV2<PlanPeriodDto>({
    queryFn: () => {
      return getActivePlanPeriod()
    },
  })
  const { data: renewalPlanPeriod } = useQueryRequestV2<RenewalPlanPeriodResponse>({
    queryFn: () => {
      return getRenewalPlanPeriod()
    },
  })

  const renewalYear = formatYear(renewalPlanPeriod?.startDate)

  const { isLg } = useBreakPoints()

  const [, setPageTitle] = useSharedState<{
    title: string
    showBackButton: boolean
  }>('PageTitle')

  const taskCallbackMapper = new Map<number, () => void>()
  taskCallbackMapper.set(IchraRenewalTaskIds.RedesignBenefit, () => {
    const url =
      renewalPlanPeriod.status === BenefitDesignStatus.Completed ||
      renewalPlanPeriod.status === BenefitDesignStatus.Signed
        ? `${ROUTE_PATHS.BENEFIT_DETAILS_PATH}?planPeriodId=${renewalPlanPeriod.id}`
        : `${ROUTE_PATHS.BENEFIT_DETAILS_PATH}`
    history.push(url)
  })
  taskCallbackMapper.set(IchraRenewalTaskIds.AssignClasses, () => history.push(ROUTE_PATHS.ASSIGN_CLASSES))
  taskCallbackMapper.set(IchraRenewalTaskIds.AcceptDocuments, () =>
    history.push(`${ROUTE_PATHS.LEGAL_DOCUMENTS_SIGN_DOCUMENTS}/${renewalPlanPeriod.id}?returnTo=RenewalTasks`),
  )
  taskCallbackMapper.set(IchraRenewalTaskIds.InviteEmployees, () => setInviteEmployeesModalVisible(true))

  useEffect(() => {
    setPageTitle({
      title: `Benefit renewal for ${renewalYear}`,
      showBackButton: true,
    })

    return () => {
      setPageTitle(null)
    }
  }, [renewalPlanPeriod])

  useEffect(() => {
    if (tasks) {
      setActiveStepIndex(tasks.findIndex((x) => x.status == TaskStatus.Active))
    }
  }, [tasks])

  return (
    <Skeleton loading={isLoading}>
      {renewalPlanPeriod && (
        <InviteIchraRenewalEmployeesModal
          visible={inviteEmployeesModalVisible}
          benefitStartDate={renewalPlanPeriod?.startDate}
          onCancel={() => setInviteEmployeesModalVisible(false)}
          onSendInvite={() => {
            window.location.href = ROUTE_PATHS.ADMIN_DASHBOARD_PATH
          }}
        />
      )}

      {tasks && (
        <>
          <div className="container-100-percent flex fd-col mt-32 inner-padding bg-white" style={{ maxWidth: 700 }}>
            <div className="main-wrapper inner-padding flex flex__column">
              {/* <div className="p-4">
                <RenewalContactWidget />
              </div> */}
              {tasks.length > 0 && (
                <>
                  <div>
                    <div className="align_self_center mt-24 mb-24 text-center">
                      <img src={Icon} alt="dashboard-logo" className={isLg ? '' : 'w-100'} />
                    </div>

                    <h1>Let’s configure your {renewalYear} ICHRA for renewal!</h1>

                    {activePlanPeriod && (
                      <p className="gray-7 mb-16">
                        To renew your benefit plan for {renewalYear}, you’ll need to either confirm or make changes to
                        your existing plan settings no later than {parseZoneFormat(activePlanPeriod.endDate)}. After
                        that date, your benefit will auto-renew using its existing settings and your employees will be
                        notified of the new benefit.
                      </p>
                    )}
                  </div>
                  <SetupProgress title={'Benefit Renewal'} steps={tasks} activeStep={activeStepIndex} />
                  <SetupProgressSteps
                    steps={tasks}
                    renewalYear={renewalYear}
                    activeStep={activeStepIndex}
                    onStepClick={(taskId) => {
                      taskCallbackMapper.get(taskId)()
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </>
      )}
    </Skeleton>
  )
}

export default IchraRenewalTasks
